import { History, Send } from 'lucide-react';

import { Button } from '@/ui/base/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';
import { EmptyState } from '@/ui/EmptyState';

import type { DreEmDefasagem } from '../contrato';

import { LinhaDeDre } from './LinhaDeDre';
import { SemRecortePorDre } from './SemRecortePorDre';

/**
 * "Cobranças DRE no mês" — o que a SME já cobrou na competência e o que ainda
 * falta cobrar.
 *
 * O CARTÃO É A MESMA LISTA DA DEFASAGEM, VISTA PELO OUTRO LADO: lá o recorte é
 * o atraso, aqui é a cobrança. A linha é a mesma (`LinhaDeDre`) porque a DRE é a
 * mesma — duas linhas separadas acabariam divergindo na contagem de lotes.
 */

interface CobrancasDoMesProps {
  dres: DreEmDefasagem[];
  cobrancasDisparadas: number;
  dresAlcancadas: number;
  /** `false` esconde as cobranças — quem não pode cobrar não vê botão nenhum. */
  podeCobrar: boolean;
  /**
   * `true` para quem não é SME: a lista compara diretorias e o sistema ainda não
   * sabe de qual diretoria é quem está olhando (ver `SemRecortePorDre`).
   */
  semRecortePorDre: boolean;
  aoCobrar: (dre: DreEmDefasagem) => void;
  aoCobrarPendentes: () => void;
}

export function CobrancasDoMes({
  dres,
  cobrancasDisparadas,
  dresAlcancadas,
  podeCobrar,
  semRecortePorDre,
  aoCobrar,
  aoCobrarPendentes,
}: CobrancasDoMesProps) {
  const pendentes = dres.filter((dre) => dre.cobrancasDisparadas === 0);

  return (
    <Card className="rounded-2xl border border-border/60">
      <CardHeader className="pb-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <CardTitle className="flex items-center gap-2 text-base">
              <History className="h-4 w-4 text-primary" aria-hidden="true" />
              Cobranças DRE no mês
            </CardTitle>
            {!semRecortePorDre && (
              <p className="mt-1 text-xs text-muted-foreground">
                {dresAlcancadas} DRE(s) cobrada(s) · {pendentes.length} pendente(s) ·{' '}
                {cobrancasDisparadas} disparo(s)
              </p>
            )}
          </div>
          {podeCobrar && !semRecortePorDre && pendentes.length > 0 && (
            <Button variant="outline" size="sm" onClick={aoCobrarPendentes}>
              <Send className="mr-1.5 h-4 w-4" aria-hidden="true" />
              Cobrar pendentes ({pendentes.length})
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {semRecortePorDre ? (
          <SemRecortePorDre />
        ) : dres.length === 0 ? (
          <EmptyState
            variant="success"
            title="Nenhuma DRE a cobrar"
            description="Todas as diretorias estão com os lotes em dia nesta competência."
          />
        ) : (
          dres.map((dre) => (
            <LinhaDeDre
              key={dre.dreId}
              dre={dre}
              tom="atencao"
              podeCobrar={podeCobrar}
              aoCobrar={aoCobrar}
            />
          ))
        )}
      </CardContent>
    </Card>
  );
}
